import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { Starburst } from "@/components/Doodles";
import { MapPin, Calendar, Phone, Mail } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const Visit = () => {
    const { t } = useContent();
    return (
        <>
            <Seo
                title="Visit"
                description="Find Panther City Clubhouse at 7264 Glenview Dr, Richland Hills, TX 76180. Soft opening Friday, November 13, 2026 — come as you are."
            />
            <PageIntro kicker="come loiter with intent" title="VISIT" intro={t("visit.intro", "One clubhouse, two little buildings, one shared patio, and a door that's about to be open.")} />

            <section className="px-5 pb-16 sm:px-10">
                <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
                    <FadeUp>
                        <div data-testid="visit-address-card" className="relative h-full border-2 border-ink bg-paper p-7 hard-shadow-lg -rotate-[0.5deg] sm:p-9">
                            <span className="tape-strip" aria-hidden="true" />
                            <MapPin className="h-8 w-8 text-orange" />
                            <h2 className="mt-3 font-display text-3xl tracking-wide text-ink">THE CLUBHOUSE</h2>
                            <p className="mt-3 text-lg font-semibold text-ink">{t("visit.address", "7264 Glenview Dr, Richland Hills, TX 76180")}</p>
                            <p className="mt-3 leading-relaxed text-ink/75">
                                {t("visit.layout", "Two buildings, one patio. The front building holds the main shared spaces, Rootbound Kava, the creative studio, and the Mirror Room. Grimsy Book Nook keeps the rear building. The patio belongs to everybody.")}
                            </p>
                            <a
                                href="https://www.google.com/maps/search/?api=1&query=7264+Glenview+Dr,+Richland+Hills,+TX+76180"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="visit-directions-link"
                                className="btn-stamp mt-6 inline-block"
                            >
                                Get Directions →
                            </a>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.1}>
                        <div data-testid="visit-opening-card" className="relative h-full border-2 border-ink bg-forest p-7 text-cream hard-shadow-lg rotate-[0.5deg] sm:p-9">
                            <span className="tape-strip" aria-hidden="true" />
                            <Calendar className="h-8 w-8 text-mustard" />
                            <h2 className="mt-3 font-display text-3xl tracking-wide">OPENING STATUS</h2>
                            <p className="mt-3 leading-relaxed text-cream/85">
                                {t("visit.status", "We're in final-paint mode. First public day: the Friday the 13th Soft Opening — Friday, November 13, 2026, come-and-go. After that, the doors settle into a rhythm; watch the calendar and the newsletter for what's open when.")}
                            </p>
                            <p className="mt-3 text-cream/75 text-sm">
                                {t("visit.hours_note", "Regular hours, parking notes, and accessibility details get posted here as they're confirmed — no guesswork.")}
                            </p>
                            <Link to="/whats-happening" data-testid="visit-events-link" className="btn-stamp mt-6 inline-block !bg-mustard !text-ink">
                                See What's On →
                            </Link>
                        </div>
                    </FadeUp>
                </div>

                <FadeUp className="mx-auto mt-8 max-w-6xl">
                    <div data-testid="visit-contact-card" className="flex flex-col items-start gap-5 border-2 border-dashed border-ink/40 bg-cream/60 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
                        <div>
                            <h2 className="font-display text-2xl tracking-wide text-ink sm:text-3xl">QUESTIONS BEFORE YOU COME?</h2>
                            <p className="mt-2 max-w-xl text-ink/75">Call, email, or tape a note to the door. We actually answer.</p>
                        </div>
                        <div className="flex flex-col gap-3">
                            <a href="tel:+1682334223" data-testid="visit-phone-link" className="flex items-center gap-2 font-display text-lg tracking-wider text-forest hover:text-brick">
                                <Phone className="h-5 w-5" /> {t("contact.phone", "(682) 334-2223")}
                            </a>
                            <a href="mailto:panthercityclubhouse@gmail.com" data-testid="visit-email-link" className="flex items-center gap-2 font-display text-lg tracking-wider text-forest hover:text-brick">
                                <Mail className="h-5 w-5" /> {t("contact.email", "panthercityclubhouse@gmail.com")}
                            </a>
                        </div>
                    </div>
                </FadeUp>

                <FadeUp className="mx-auto mt-14 max-w-3xl text-center">
                    <Starburst className="mx-auto h-24 w-24 text-orange" lines={["SEE YOU", "SOON"]} />
                    <p className="mt-5 font-hand text-2xl text-brick -rotate-1 sm:text-3xl">first rule of the clubhouse: come as you are.</p>
                </FadeUp>
            </section>

            <Marquee />
            <NewsletterSection idPrefix="visit-newsletter" tone="forest" />
        </>
    );
};

export default Visit;
